import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Container,
  TextField,
  Box,
  IconButton,
  Paper,
  CircularProgress,
  Snackbar,
  Alert,
  AlertColor,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePasskey } from '../contexts/PasskeyContext';
import { getUserThoughts, getUserLabels, updateThought, deleteThought, Thought } from '../services/thoughtService';
import Header from '../components/Header';
import LabelSelector from '../components/LabelSelector';

interface SnackbarState {
  open: boolean;
  message: string;
  severity: AlertColor;
}

const ThoughtEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const { passkey } = usePasskey();
  
  const [thought, setThought] = useState<Thought | null>(null);
  const [title, setTitle] = useState<string>('');
  const [content, setContent] = useState<string>('');
  const [labels, setLabels] = useState<string[]>([]);
  const [availableLabels, setAvailableLabels] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [snackbar, setSnackbar] = useState<SnackbarState>({ 
    open: false, 
    message: '', 
    severity: 'success' 
  });
  const [saveStatus, setSaveStatus] = useState<'saved' | 'saving' | 'dirty' | null>(null);
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    loadThought();
    loadAvailableLabels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, currentUser]);

  const loadAvailableLabels = async () => {
    if (!currentUser) return;
    try {
      const fetchedLabels = await getUserLabels(currentUser.uid);
      setAvailableLabels(fetchedLabels);
    } catch (error) {
      console.error('Error loading labels:', error);
    }
  };

  const loadThought = async () => {
    if (!currentUser || !id || !passkey) {
      console.error('❌ Cannot load thought: missing user, id, or passkey');
      return;
    }
    
    // Check if we have initial data from navigation state (new thought)
    const initialData = location.state as { initialTitle?: string; initialContent?: string; initialLabels?: string[] } | null;
    
    if (initialData) {
      console.log('🆕 Using initial data from navigation state (new thought)');
      setThought({
        id: id,
        userId: currentUser.uid,
        title: initialData.initialTitle || '',
        content: initialData.initialContent || '',
        labels: initialData.initialLabels || [],
        createdAt: null as any,
        updatedAt: null as any,
      });
      setTitle(initialData.initialTitle || '');
      setContent(initialData.initialContent || '');
      setLabels(initialData.initialLabels || []);
      setLoading(false);
      
      // Clear navigation state so next time it loads from Firestore
      window.history.replaceState({}, document.title);
      console.log('✨ Cleared navigation state - next load will fetch from Firestore');
      return;
    }
    
    try {
      setLoading(true);
      console.log('📖 ThoughtEditor: Loading thought', id, 'with passkey length:', passkey.length);
      const thoughts = await getUserThoughts(currentUser.uid, passkey);
      console.log(`📚 Loaded ${thoughts.length} total thoughts`);
      
      const foundThought = thoughts.find(t => t.id === id);
      
      if (foundThought) {
        console.log('✅ Found thought:', { 
          id: foundThought.id, 
          title: foundThought.title.substring(0, 50),
          isLocked: foundThought.title === '🔒 Locked'
        });
        
        if (foundThought.title === '🔒 Locked') {
          console.error('⚠️ WARNING: Thought is locked! This means decryption failed.');
          console.error('   Check if the passkey used to create this thought matches the current passkey');
        }
        
        setThought(foundThought);
        setTitle(foundThought.title || '');
        setContent(foundThought.content || '');
        setLabels(foundThought.labels || []);
      } else {
        console.error('❌ Thought not found in results');
        setSnackbar({ open: true, message: 'Thought not found', severity: 'error' });
        navigate('/');
      }
    } catch (error) {
      console.error('Error loading thought:', error);
      setSnackbar({ open: true, message: 'Error loading thought', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Auto-save function
  const autoSave = useCallback(async (updates: { title?: string; content?: string; labels?: string[] }) => {
    if (!id || !passkey) {
      console.error('❌ Cannot auto-save: missing id or passkey');
      return;
    }
    
    try {
      setSaveStatus('saving');
      console.log('💾 Auto-saving thought', id, 'with updates:', updates);
      await updateThought(id, updates, passkey);
      console.log('✅ Auto-saved successfully');
      setSaveStatus('saved');
      
      // Hide "saved" indicator after 2 seconds
      setTimeout(() => {
        setSaveStatus(null);
      }, 2000);
    } catch (error) {
      console.error('❌ Auto-save error:', error);
      setSaveStatus(null);
    }
  }, [id, passkey]);

  // Debounced auto-save (waits 500ms after typing stops)
  const debouncedAutoSave = useCallback((updates: { title?: string; content?: string; labels?: string[] }) => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }
    
    setSaveStatus('dirty'); // Mark as dirty when content changes
    
    autoSaveTimeoutRef.current = setTimeout(() => {
      autoSave(updates);
    }, 500);
  }, [autoSave]);

  // Auto-save on title change
  useEffect(() => {
    // Don't auto-save if thought is locked
    if (thought && id && title !== undefined && thought.title !== '🔒 Locked') {
      debouncedAutoSave({ title: title || 'Untitled Thought' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title]);

  // Auto-save on content change
  useEffect(() => {
    // Don't auto-save if thought is locked
    if (thought && id && content !== undefined && thought.title !== '🔒 Locked') {
      debouncedAutoSave({ content });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content]);

  const handleSave = async () => {
    if (!id) return;
    
    try {
      setSaving(true);
      await updateThought(id, {
        title: title || 'Untitled Thought',
        content,
        labels
      });
      setSnackbar({ open: true, message: 'Thought saved successfully', severity: 'success' });
    } catch (error) {
      console.error('Error saving thought:', error);
      setSnackbar({ open: true, message: 'Error saving thought', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    
    try {
      await deleteThought(id);
      setSnackbar({ open: true, message: 'Thought deleted', severity: 'success' });
      navigate('/');
    } catch (error) {
      console.error('Error deleting thought:', error);
      setSnackbar({ open: true, message: 'Error deleting thought', severity: 'error' });
    }
    setDeleteDialogOpen(false);
  };

  const handleLabelsChange = async (newLabels: string[]) => {
    setLabels(newLabels);
    
    // Auto-save labels immediately (only if not locked)
    if (id && passkey && thought?.title !== '🔒 Locked') {
      try {
        setSaveStatus('saving');
        await updateThought(id, { labels: newLabels }, passkey);
        setSaveStatus('saved');
        
        // Reload available labels
        loadAvailableLabels();
        
        // Hide "saved" indicator after 2 seconds
        setTimeout(() => {
          setSaveStatus(null);
        }, 2000);
      } catch (error) {
        console.error('Error saving labels:', error);
        setSaveStatus(null);
        setSnackbar({ open: true, message: 'Error saving labels', severity: 'error' });
      }
    }
  };

  const handleBack = () => {
    navigate('/');
  };

  if (loading) {
    return (
      <>
        <Header />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh', mt: 10 }}>
          <CircularProgress />
        </Box>
      </>
    );
  }

  // Show locked thought UI
  if (thought?.title === '🔒 Locked') {
    return (
      <>
        <Header />
        <Container maxWidth="md" sx={{ mt: 10, mb: 4 }}>
          <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h3" sx={{ mb: 2 }}>🔒</Typography>
            <Typography variant="h5" gutterBottom>This Thought is Locked</Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              This thought was encrypted with a different passkey and cannot be decrypted with your current passkey.
            </Typography>
            <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button variant="outlined" onClick={() => navigate('/')}>
                Go Back
              </Button>
              <Button 
                variant="contained" 
                color="error" 
                onClick={() => setDeleteDialogOpen(true)}
              >
                Delete This Thought
              </Button>
            </Box>
          </Paper>
        </Container>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>Delete Locked Thought?</DialogTitle>
          <DialogContent>
            <Typography>
              This thought cannot be decrypted. Delete it permanently?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleDelete} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  return (
    <>
      <Header />
      
      <Container maxWidth="md" sx={{ mt: 10, mb: 4 }}>
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={handleBack}>
            <ArrowBackIcon />
          </IconButton>
          
          {/* Labels in header */}
          <Box sx={{ flexGrow: 1, maxWidth: 400 }}>
            <LabelSelector
              availableLabels={availableLabels}
              selectedLabels={labels}
              onLabelsChange={handleLabelsChange}
            />
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {/* Save Status Indicator - Small icon */}
            {saveStatus === 'saved' && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  bgcolor: 'success.main',
                  color: 'white',
                  animation: 'fadeIn 0.3s ease-in',
                  '@keyframes fadeIn': {
                    from: { opacity: 0, transform: 'scale(0.5)' },
                    to: { opacity: 1, transform: 'scale(1)' }
                  }
                }}
              >
                <CheckIcon sx={{ fontSize: 16 }} />
              </Box>
            )}
            
            <IconButton
              color="error"
              onClick={() => setDeleteDialogOpen(true)}
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        </Box>

        <Paper elevation={2} sx={{ p: 3 }}>
          <TextField
            fullWidth
            variant="standard"
            placeholder="Thought Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            sx={{
              mb: 3,
              '& .MuiInputBase-input': {
                fontSize: '2rem',
                fontWeight: 600
              }
            }}
          />

          <TextField
            fullWidth
            multiline
            variant="standard"
            placeholder="Start writing your thought..."
            value={content}
            onChange={(e) => setContent(e.target.value)}
            minRows={15}
            sx={{
              '& .MuiInputBase-input': {
                fontSize: '1.1rem',
                lineHeight: 1.8
              }
            }}
          />
        </Paper>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>Delete Thought?</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete this thought? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleDelete} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </>
  );
};

export default ThoughtEditor;

